/**
 * The single source of truth for layout density.
 *
 * Three values, and the third is why this is a store rather than a `useState`
 * in the panel that used to own it. `compact` and `comfortable` were a setting
 * chosen once, in one place; `tight` is a control an operator reaches for in the
 * middle of a shift, from the top bar, to take back the pixels the chrome is
 * spending. Two controls now write it, and a local boolean in either would show
 * the opposite of what is on screen the moment the other was used — the exact
 * defect `useTheme` grew an external store to fix, arrived at again by the same
 * road.
 *
 * Every reader is the DOM attribute, not this module's memory: `main.tsx`
 * replays the stored value before first paint, and `TerminalPane` watches the
 * attribute to refit its terminal. So a setter's job is to write the attribute
 * and the key, and tell React to look again.
 *
 * The top bar's control is tight ⇄ *what the operator chose*, never tight ⇄
 * compact. Somebody who set Rahat and presses it twice has to land back on
 * Rahat; anything else is the console quietly discarding a choice somebody made,
 * which is the one thing this codebase does not do to a person's input. That is
 * what `RELAXED_KEY` remembers, written only on the way into tight.
 */
import { useSyncExternalStore } from 'react'

export type Density = 'tight' | 'compact' | 'comfortable'

/** The default, and the one this console shipped with before there were three. */
const DEFAULT: Density = 'compact'

const KEY = 'kimene.density'
/** The last density that was not tight — what the top bar's toggle returns to. */
const RELAXED_KEY = 'kimene.densityRelaxed'

const listeners = new Set<() => void>()

function parse(value: string | null | undefined): Density | null {
  return value === 'tight' || value === 'compact' || value === 'comfortable' ? value : null
}

export function currentDensity(): Density {
  return parse(document.documentElement.dataset.density) ?? DEFAULT
}

/** What "normal" means for this operator: the last density they chose that was
 *  not tight. Compact when they have never chosen one. */
function relaxedDensity(): Density {
  return parse(localStorage.getItem(RELAXED_KEY)) ?? DEFAULT
}

/** Apply a density and notify every reader (this tab) — storage events cover
 *  others. */
export function setDensity(next: Density): void {
  const prev = currentDensity()
  // Remembered on the way in rather than derived on the way out: once the stored
  // density says `tight`, what it was before is gone, and the toggle would have
  // to guess. Only on the way in, so pressing the control twice cannot overwrite
  // the answer with `tight` itself.
  if (next === 'tight' && prev !== 'tight') localStorage.setItem(RELAXED_KEY, prev)
  document.documentElement.dataset.density = next
  localStorage.setItem(KEY, next)
  listeners.forEach((l) => l())
}

/** The top bar's control: into the spreadsheet, or back to whatever the operator
 *  had before it. */
export function toggleTight(): void {
  setDensity(currentDensity() === 'tight' ? relaxedDensity() : 'tight')
}

function subscribe(cb: () => void) {
  listeners.add(cb)
  const onStorage = (e: StorageEvent) => {
    if (e.key !== KEY) return
    // The attribute is applied here, not just re-read. Another tab's write
    // changes this tab's localStorage and nothing else, so a handler that only
    // notified would re-read an attribute that had not moved and conclude that
    // nothing had happened — a cross-tab sync that silently does nothing is
    // worse than none, because the comment says it works.
    const next = parse(e.newValue)
    if (next) document.documentElement.dataset.density = next
    cb()
  }
  window.addEventListener('storage', onStorage)
  return () => {
    listeners.delete(cb)
    window.removeEventListener('storage', onStorage)
  }
}

/** Reads the current density and re-renders when it changes anywhere. */
export function useDensity(): [Density, (d: Density) => void] {
  const density = useSyncExternalStore(subscribe, currentDensity, (): Density => DEFAULT)
  return [density, setDensity]
}
